import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin, Progress, Input, Tag } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { TeamOutlined, PercentageOutlined, SearchOutlined, FileTextOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import dayjs from 'dayjs';
import * as actions from 'store/actions/AuthActions';
import useIsMobile from '../../hooks/useIsMobile';
import ReportFilterModal, { DatePresetButtons, MobileFilterButton, getDateRangeFromPreset } from '../../components/Reports/ReportFilterModal';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from '../../hooks/useUrlFilters';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Filters live in the URL so a filtered report can be shared as a link.
// Relative presets are kept as the preset name; only custom and FY ranges carry dates.
const URL_FILTERS = {
  preset: { type: 'string', default: 'last_30_days' },
  range: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  employee: { type: 'string' },
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 10 },
};
const PRESETS_WITH_DATES = ['custom', 'this_fy', 'last_fy'];
// Relative presets are recomputed from the name; a custom range with no dates is empty.
const resolveRange = (preset, range) =>
  (PRESETS_WITH_DATES.includes(preset) && range) || (preset === 'custom' ? null : getDateRangeFromPreset(preset));

const DailyOperationsReport = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const filterPreset = filters.preset;
  const dateRange = useMemo(() => resolveRange(filterPreset, filters.range), [filterPreset, filters.range]);
  const [loading, setLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const employeeFilter = filters.employee;
  const setEmployeeFilter = (value) => setFilters({ employee: value, page: 1 });
  const [employees, setEmployees] = useState([]);
  const searchValue = filters.search || '';
  const setSearchValue = (value) => setFilters({ search: value, page: 1 });

  // Fetch analytics data
  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.date_from = dateRange[0].format('YYYY-MM-DD');
        filters.date_to = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await actions.getDailyUpdatesAnalytics(filters);
      if (response) {
        setAnalyticsData(response);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch daily updates list
  const fetchUpdates = useCallback(async (page = 1, pageSize = 10) => {
    setUpdatesLoading(true);
    try {
      const filters = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.update_date_from = dateRange[0].format('YYYY-MM-DD');
        filters.update_date_to = dateRange[1].format('YYYY-MM-DD');
      }
      if (employeeFilter) {
        filters.employee = employeeFilter;
      }
      if (searchValue) {
        filters.search = searchValue;
      }
      const response = await actions.loadDailyUpdates(page, pageSize, filters);
      if (response) {
        setUpdates(response.results || []);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: response.count || 0
        });
      }
    } catch (error) {
      console.error('Error fetching updates:', error);
    } finally {
      setUpdatesLoading(false);
    }
  }, [dateRange, employeeFilter, searchValue]);

  // Fetch employees for filter
  const fetchEmployees = async () => {
    try {
      const response = await actions.loadAllEmployees();
      if (response && response.data) {
        setEmployees(response.data.results || response.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Fetch analytics when date range changes
  useEffect(() => {
    if (dateRange) {
      fetchAnalytics();
    }
  }, [dateRange, fetchAnalytics]);

  // Fetch updates when any filter or the page changes
  useEffect(() => {
    if (dateRange) {
      fetchUpdates(filters.page, Math.min(filters.pageSize, MAX_PAGE_SIZE));
    }
  }, [dateRange, fetchUpdates, filters.page, filters.pageSize]);

  // Handle preset change - using helper function
  const handlePresetChange = (preset) => {
    setFilters({
      preset,
      range: PRESETS_WITH_DATES.includes(preset) ? getDateRangeFromPreset(preset) : null,
      page: 1,
    });
  };

  // Handle date range change
  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setFilters({ preset: 'custom', range: [dates[0], dates[1]], page: 1 });
    } else {
      setFilters({ preset: 'all', range: null, page: 1 });
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ preset: 'last_30_days', range: null, employee: null, search: null, page: 1 });
  };

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (filterPreset !== 'all' && filterPreset !== 'last_30_days') count++;
    if (employeeFilter) count++;
    if (searchValue) count++;
    return count;
  };

  // Handle table change
  const handleTableChange = (newPagination) => {
    // A new page size starts again from page 1
    const sizeChanged = newPagination.pageSize !== filters.pageSize;
    setFilters({ page: sizeChanged ? 1 : newPagination.current, pageSize: newPagination.pageSize });
  };

  // Prepare chart data
  const getChartData = () => {
    if (!analyticsData?.updates_per_day) return [];
    return analyticsData.updates_per_day.map(item => ({
      date: dayjs(item.date).format('MMM DD'),
      updates: item.count
    }));
  };

  // Prepare department chart data
  const getDepartmentChartData = () => {
    if (!analyticsData?.department_breakdown) return [];
    return analyticsData.department_breakdown.map(item => ({
      department: item.employee__department || 'Unassigned',
      count: item.count
    }));
  };

  // Table columns with responsive configuration
  const columns = [
    {
      title: 'Date',
      dataIndex: 'update_date',
      key: 'update_date',
      width: 100,
      render: (date) => dayjs(date).format(isMobile ? 'MM/DD' : 'MMM DD, YYYY'),
      sorter: (a, b) => dayjs(a.update_date).unix() - dayjs(b.update_date).unix()
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      width: isMobile ? 120 : 180,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: isMobile ? '12px' : '14px' }}>{name}</Text>
          {!isMobile && <Text type="secondary" style={{ fontSize: '12px' }}>{record.department}</Text>}
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status) => status === 'SUBMITTED'
        ? <Tag color="green">Submitted</Tag>
        : <Tag color="orange">Draft</Tag>,
    },
    {
      title: 'Tasks Completed',
      dataIndex: 'tasks_completed',
      key: 'tasks_completed',
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Challenges',
      dataIndex: 'challenges',
      key: 'challenges',
      responsive: ['md'],
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>
    },
    {
      title: 'Plan for Tomorrow',
      dataIndex: 'plan_for_tomorrow',
      key: 'plan_for_tomorrow',
      responsive: ['lg'],
      render: (text) => text ? (
        <div
          style={{ fontSize: isMobile ? '12px' : '13px' }}
          dangerouslySetInnerHTML={{ __html: text }}
        />
      ) : <Text type="secondary">-</Text>
    }
  ];

  // Render additional filters for modal
  const renderAdditionalFilters = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Employee</Text>
        <Select
          value={employeeFilter}
          onChange={setEmployeeFilter}
          style={{ width: '100%' }}
          placeholder="All Employees"
          allowClear
          showSearch
          optionFilterProp="children"
        >
          {employees.map(emp => (
            <Option key={emp.id} value={emp.id}>
              {emp.user_name || emp.employee_id}
            </Option>
          ))}
        </Select>
      </div>
      <div>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>Search</Text>
        <Input
          placeholder="Search updates..."
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ width: '100%' }}
          allowClear
        />
      </div>
    </Space>
  );

  // Tables for the PDF export. The updates table is paginated server-side, so
  // the export carries the page on screen and says so.
  const pdfTables = [
    {
      title: 'Updates by Department',
      columns: [
        { header: 'Department', key: 'employee__department', format: (value) => value || 'Unassigned' },
        { header: 'Updates', key: 'count', align: 'right' },
      ],
      rows: analyticsData?.department_breakdown || [],
    },
    {
      title: 'Updates Over Time',
      columns: [
        { header: 'Date', key: 'date' },
        { header: 'Updates', key: 'count', align: 'right' },
      ],
      rows: analyticsData?.updates_per_day || [],
    },
    {
      title: 'Daily Updates',
      note: 'Server-paginated: the rows currently loaded are included.',
      columns: [
        { header: 'Date', key: 'update_date' },
        { header: 'Employee', key: 'employee_name' },
        { header: 'Status', key: 'status' },
        { header: 'Tasks Completed', key: 'tasks_completed' },
        { header: 'Challenges', key: 'challenges' },
        { header: 'Plan for Tomorrow', key: 'plan_for_tomorrow' },
      ],
      rows: updates || [],
    },
  ];

  const pdfMeta = [
    { label: 'Total Updates', value: analyticsData?.total_updates || 0 },
    { label: 'Employees with Updates', value: `${analyticsData?.employees_with_updates || 0} / ${analyticsData?.total_employees || 0}` },
    { label: 'Participation Rate', value: `${analyticsData?.participation_rate || 0}%` },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Daily Operations Report</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Daily Operations Report"
            subtitle={dateRange && dateRange[0] && dateRange[1]
              ? `${dateRange[0].format('YYYY-MM-DD')} to ${dateRange[1].format('YYYY-MM-DD')}`
              : 'All time'}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="daily-operations-report"
            orientation="landscape"
            disabled={loading || !analyticsData}
          />
          {isMobile && (
            <MobileFilterButton
              onClick={() => setFilterModalVisible(true)}
              activeFiltersCount={getActiveFiltersCount()}
            />
          )}
        </div>
      </div>

      {/* Desktop Filters */}
      {!isMobile && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <div>
              <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Date Range:</Text>
              <DatePresetButtons filterPreset={filterPreset} onPresetChange={handlePresetChange} />
            </div>
            <div>
              <Space wrap size="middle">
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Custom Date Range:</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    size="small"
                    allowClear
                    placeholder={['Start Date', 'End Date']}
                  />
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Employee:</Text>
                  <Select
                    value={employeeFilter}
                    onChange={setEmployeeFilter}
                    style={{ width: 200 }}
                    size="small"
                    placeholder="All Employees"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                  >
                    {employees.map(emp => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.user_name || emp.employee_id}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block', fontSize: '13px' }}>Search:</Text>
                  <Input
                    placeholder="Search..."
                    prefix={<SearchOutlined />}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    style={{ width: 200 }}
                    size="small"
                    allowClear
                  />
                </div>
              </Space>
            </div>
          </Space>
        </Card>
      )}

      {/* Mobile Filter Modal */}
      <ReportFilterModal
        visible={filterModalVisible}
        onClose={() => setFilterModalVisible(false)}
        filterPreset={filterPreset}
        dateRange={dateRange}
        onChange={({ preset, range }) => setFilters({
          preset,
          range: PRESETS_WITH_DATES.includes(preset) ? range : null,
          page: 1,
        })}
        onClearFilters={handleClearFilters}
        title="Daily Operations Filters"
      >
        {renderAdditionalFilters()}
      </ReportFilterModal>

      {/* Statistics Cards */}
      <Spin spinning={loading}>
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Updates</span>}
                value={analyticsData?.total_updates || 0}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '20px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Employees with Updates</span>}
                value={analyticsData?.employees_with_updates || 0}
                suffix={`/ ${analyticsData?.total_employees || 0}`}
                prefix={<TeamOutlined />}
                valueStyle={{ fontSize: isMobile ? '16px' : '20px' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Statistic
                title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Participation Rate</span>}
                value={analyticsData?.participation_rate || 0}
                suffix="%"
                prefix={<PercentageOutlined />}
                valueStyle={{
                  color: analyticsData?.participation_rate >= 80 ? '#52c41a' : analyticsData?.participation_rate >= 50 ? '#faad14' : '#ff4d4f',
                  fontSize: isMobile ? '16px' : '20px'
                }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={12} lg={6}>
            <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>Participation Progress</Text>
                <Progress
                  percent={analyticsData?.participation_rate || 0}
                  status={analyticsData?.participation_rate >= 80 ? 'success' : analyticsData?.participation_rate >= 50 ? 'normal' : 'exception'}
                  showInfo={false}
                  size={isMobile ? 'small' : 'default'}
                />
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Charts */}
        <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
          <Col xs={24} lg={16}>
            <Card
              size="small"
              title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Updates Over Time</Text>}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <LineChart
                  data={getChartData()}
                  margin={{ top: 10, right: isMobile ? 10 : 30, left: isMobile ? 0 : 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? 'end' : 'middle'}
                    height={isMobile ? 60 : 30}
                  />
                  <YAxis
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                    width={isMobile ? 30 : 40}
                  />
                  <RechartsTooltip />
                  <Line
                    type="monotone"
                    dataKey="updates"
                    stroke="#1890ff"
                    strokeWidth={2}
                    dot={{ fill: '#1890ff', strokeWidth: 2, r: isMobile ? 3 : 4 }}
                    activeDot={{ r: isMobile ? 5 : 6, fill: '#1890ff' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              size="small"
              title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Updates by Department</Text>}
            >
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <BarChart
                  data={getDepartmentChartData()}
                  layout="vertical"
                  margin={{ top: 10, right: isMobile ? 10 : 30, left: isMobile ? 0 : 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: isMobile ? 10 : 12 }} />
                  <YAxis
                    dataKey="department"
                    type="category"
                    width={isMobile ? 70 : 100}
                    tick={{ fontSize: isMobile ? 10 : 12 }}
                  />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Updates Table */}
      <Card
        size="small"
        title={<Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>Daily Updates</Text>}
      >
        <Table
          columns={columns}
          dataSource={updates}
          rowKey="id"
          loading={updatesLoading}
          pagination={{
            ...pagination,
            defaultPageSize: isMobile ? 5 : 10,
            pageSizeOptions: ['5', '10', '20', '50'],
            showSizeChanger: !isMobile,
            showTotal: isMobile ? undefined : (total, range) => `${range[0]}-${range[1]} of ${total} updates`,
            position: ['bottomRight'],
            responsive: true,
            simple: isMobile,
            style: { marginTop: '16px', marginBottom: '8px' }
          }}
          onChange={handleTableChange}
          size="small"
          scroll={{ x: isMobile ? 400 : 1000 }}
        />
      </Card>
    </div>
  );
};

export default DailyOperationsReport;
